import { useRecoilState } from 'recoil'
import { useEffect } from 'react'
import { currencies } from '~/src/constants/currencies'
import { currencyState } from '~/src/recoil/atoms/currencyAtom'
import { settingCurrencyRepository } from '~/src/repositories'
import { useMessage } from '~/src/hooks'

/**
 * useCurrency
 * Recoilを使用した通貨データ管理フック
 * @example
 * ```ts
 * const { currencies, baseCurrency, baseCurrencyUnit, unitPosition, isLoading, refetch } = useCurrency()
 * ```
 */
export const useCurrency = () => {
  const [currencyData, setCurrencyData] = useRecoilState(currencyState)
  const { handleOpenWithErrorMessage } = useMessage()

  const fetchCurrencies = async () => {
    try {
      setCurrencyData(prev => ({ ...prev, isLoading: true, error: null }))
      const response = await settingCurrencyRepository.getCurrencies()

      const currency = currencies.filter(currency => currency.id === response.data[0].baseCurrency.id)[0]
      if (response.data && response.data.length > 0) {
        setCurrencyData({
          currencies: response.data,
          baseCurrency: response.data[0].baseCurrency,
          baseCurrencyUnit: currency?.unit,
          unitPosition: currency?.position,
          isLoading: false,
          error: null,
        })
      }
    } catch (err: any) {
      const errorMessage = err.message || 'Failed to fetch currencies'
      setCurrencyData(prev => ({
        ...prev,
        isLoading: false,
        error: errorMessage,
      }))
      handleOpenWithErrorMessage(errorMessage)
    }
  }

  useEffect(() => {
    if (currencyData.currencies.length === 0 && !currencyData.isLoading) {
      fetchCurrencies()
    }
  }, [])

  return {
    currencies: currencyData.currencies,
    baseCurrency: currencyData.baseCurrency,
    baseCurrencyUnit: currencyData.baseCurrencyUnit,
    unitPosition: currencyData.unitPosition,
    isLoading: currencyData.isLoading,
    error: currencyData.error,
    refetch: fetchCurrencies,
  }
}
